import { defineStore } from 'pinia'
import { ref } from 'vue'
import apiClient from '@/api/client'

export interface ActiveSeeds {
  server_seed_hash: string
  client_seed: string
  nonce: number
}

export interface RotationResult {
  old_server_seed: string
  old_server_seed_hash: string
  new_seed_hash: string
  new_client_seed: string
}

export interface SeedHistoryEntry {
  id: number
  server_seed: string
  server_seed_hash: string
  client_seed: string
  nonce: number
  revealed_at: string | null
  created_at: string | null
}

export interface SeedHistoryMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

export const useProvablyFairStore = defineStore('provablyFair', () => {
  // Active seed state
  const activeSeeds = ref<ActiveSeeds | null>(null)
  const loadingActive = ref(false)
  const activeError = ref<string | null>(null)

  // Client seed update state
  const updatingClientSeed = ref(false)
  const clientSeedError = ref<string | null>(null)

  // Rotation state
  const rotating = ref(false)
  const rotationError = ref<string | null>(null)
  const lastRotation = ref<RotationResult | null>(null)

  // History state
  const history = ref<SeedHistoryEntry[]>([])
  const historyMeta = ref<SeedHistoryMeta>({ current_page: 1, last_page: 1, per_page: 10, total: 0 })
  const loadingHistory = ref(false)
  const historyError = ref<string | null>(null)

  async function fetchActiveSeeds() {
    loadingActive.value = true
    activeError.value = null

    try {
      const response = await apiClient.get('/api/provably-fair/active')
      activeSeeds.value = response.data
    } catch {
      activeError.value = 'Failed to load seed information'
    } finally {
      loadingActive.value = false
    }
  }

  async function updateClientSeed(seed: string): Promise<boolean> {
    updatingClientSeed.value = true
    clientSeedError.value = null

    try {
      const response = await apiClient.post('/api/provably-fair/client-seed', {
        client_seed: seed,
      })
      activeSeeds.value = response.data
      return true
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      clientSeedError.value = axiosErr.response?.data?.message || 'Failed to update client seed'
      return false
    } finally {
      updatingClientSeed.value = false
    }
  }

  async function rotateSeed(): Promise<boolean> {
    rotating.value = true
    rotationError.value = null
    lastRotation.value = null

    try {
      const response = await apiClient.post('/api/provably-fair/rotate')
      lastRotation.value = response.data

      // Update active seeds to reflect new state
      if (activeSeeds.value) {
        activeSeeds.value = {
          server_seed_hash: response.data.new_seed_hash,
          client_seed: response.data.new_client_seed,
          nonce: 0,
        }
      }

      return true
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      rotationError.value = axiosErr.response?.data?.message || 'Failed to rotate seed'
      return false
    } finally {
      rotating.value = false
    }
  }

  async function fetchHistory(page = 1) {
    loadingHistory.value = true
    historyError.value = null

    try {
      const response = await apiClient.get('/api/provably-fair/history', {
        params: { page, limit: 10 },
      })
      history.value = response.data.data
      historyMeta.value = response.data.meta
    } catch {
      historyError.value = 'Failed to load seed history'
    } finally {
      loadingHistory.value = false
    }
  }

  function clearRotation() {
    lastRotation.value = null
  }

  return {
    activeSeeds,
    loadingActive,
    activeError,
    updatingClientSeed,
    clientSeedError,
    rotating,
    rotationError,
    lastRotation,
    history,
    historyMeta,
    loadingHistory,
    historyError,
    fetchActiveSeeds,
    updateClientSeed,
    rotateSeed,
    fetchHistory,
    clearRotation,
  }
})
